import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, User, CheckCircle, MessageSquare, Hammer, FileDown, BarChart2, FileText, Building2, FileBadge, Map, ArrowRight } from 'lucide-react';
import Footer from '../Footer/Footer';
import LandingAboutus from '../landingaboutus/LandingAboutus';
import PropertiesService from '../../Services/PropertiesService';
import Modal from '../Modal/Modal';
import CustomModel from '../CustomModel/CustomModel';
import DocitySeal from '../Common/DocitySeal';
import partner1 from '../../assets/Images/Dashboard/partner1.svg';
import partner2 from '../../assets/Images/Dashboard/partner2.svg';
import sampleReportPdf from '../../assets/Images/merged-document.pdf';
import logo from '../../assets/Images/LOGOS/Docity_Logo_updatedDocity_Text_Logo_Colored.png'
import { getServicesIntroDismissedKey } from '../../utils/onboardingStorage';

/* ---------------------------------------------------------------------------
   The three services, and the six process steps.
   ---------------------------------------------------------------------------
   Both were three/six near-identical hand-written JSX blocks. Lifting them into
   data means the cards cannot drift apart from one another, and adding or
   reordering one is a one-line change.

   COPY NOTE. The service descriptions were previously first-person form
   declarations pasted out of the submission flow into third-person marketing —
   the DCP card literally read "I am above 18 years old. My Aadhaar number",
   which is a consent checkbox and an input label, and it trailed off mid
   sentence. All three also repeated the card title as a prefix, which the card
   already renders directly above.

   They are now "who it is for" + "what you receive", and every claim is
   traceable to what the app actually does:
     • DCP — engineer assignment, sale deed review, market valuation.
     • SOC — ownership certificate bearing the DoCity Seal. Worded as "issued on
       your declaration" to match the certificate's own text, which accepts the
       submission "as true to the best of the applicant's knowledge". It is a
       self-declaration, not an independent verification of title, and the copy
       must not imply otherwise.
     • KYP — the four categories the KYP form actually collects (land use,
       market value, neighbour details, bank loan) plus the master plan.
   --------------------------------------------------------------------------- */
const SERVICES = [
    {
        key: 'dcp',
        abbr: 'DCP',
        title: 'DCP: DoCity Customer Property (for buyers)',
        icon: Building2,
        route: '/requestProperty',
        popular: true,
        description:
            'Before you commit to a purchase, a civil engineer reviews the saledeed (the legal document that records the sale), the ownership chain (the history of who has owned the property), and the landuse documents (what the land is legally allowed to be used for). You get a clear verdict and a market valuation, so you know whether the price is fair.',
    },
    {
        key: 'soc',
        abbr: 'SOC',
        title: `SOC: Self Occupation Certificate
(Self- Declaration for Property Owners)`,
        icon: FileBadge,
        route: '/soc',
        popular: false,
        description:
            'Already own your property and want it properly documented. Build a secure digital archive of your property papers and receive an ownership certificate carrying the DoCity Seal, issued on your own declaration.',
    },
    {
        key: 'kyp',
        abbr: 'KYP',
        title: 'KYP: Know Your Property (for property owners)',
        icon: Map,
        route: '/kyp',
        popular: false,
        description:
            'KYP gives property owners a clearer and more structured way to know and maintain their property information. By bringing essential property details and supporting documents together in one digital journey, KYP helps create a reliable property record that is easy to access and preserve. Once the required information, Aadhaar verification and payment are completed, a KYP certificate is generated through DoCity giving the owner a convenient digital record of their property information.',
    },
];

const PROCESS_STEPS = [
    {
        icon: User,
        title: 'Login / Register',
        desc: 'Sign up on DoCity with our quick registration, then submit your property details through our verification form.',
    },
    {
        icon: FileText,
        title: 'Upload documents',
        desc: 'Submit key property documents including saledeeds, landuse certificates, and ownership chain documentation.',
    },
    {
        icon: CheckCircle,
        title: 'Acknowledgement',
        desc: 'Complete a small processing payment to secure your dedicated civil engineer and begin your verification process.',
    },
    {
        icon: MessageSquare,
        title: 'Communication',
        desc: 'Connect with your assigned engineer via our portal chat or direct communication to resolve any questions.',
    },
    {
        icon: Hammer,
        title: 'Processing',
        desc: 'Your DoCity engineer thoroughly evaluates your documents and conducts a comprehensive assessment of the property.',
    },
    {
        icon: FileDown,
        title: 'Report',
        desc: 'Access your comprehensive final report on our portal, featuring detailed property analysis and market valuation.',
    },
];

const Dashboard = () => {
    const navigate = useNavigate();
    const [propertyRequestSwitch, setPropertyRequestSwitch] = useState('');
    const [token, setToken] = useState(() => localStorage.getItem('token') || '');
    const [ModalOpen, setModalOpen] = useState(false);
    const [showServicesIntroPopup, setShowServicesIntroPopup] = useState(false);
    const [showDcpServicesPopup, setShowDcpServicesPopup] = useState(false);
    const [PaymentModel, setPaymentModel] = useState(false);
    const openModal = () => setModalOpen(true);
    const closeModal = () => {
        const currentToken = localStorage.getItem('token') || '';
        setToken(currentToken);
        setModalOpen(false);
        setIsSignup("firstpage");

    };
    const [otpVerification, setOtpVerification] = useState(false);
    const [isSignup, setIsSignup] = useState("firstpage");
    const dismissServicesIntro = () => {
        localStorage.setItem(getServicesIntroDismissedKey(), 'true');
        setShowServicesIntroPopup(false);
    };

    const handleServiceEntry = (route) => {
        setShowDcpServicesPopup(false);
        // Read the session at click time because it may change while this page
        // remains mounted after a sign-in or sign-out.
        const currentToken = localStorage.getItem('token') || '';
        setToken(currentToken);

        if (currentToken) {
            navigate(route);
            return;
        }

        setIsSignup('firstpage');
        openModal();
    };

    useEffect(() => {
        setToken(localStorage.getItem('token') || '')
        const sections = document.querySelectorAll('.process-section, .partners-section, .testimonials-section, .landing-aboutus-section, .welcome-section');

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fadeIn');
                }
            });
        }, observerOptions);

        sections.forEach((section) => {
            observer.observe(section);
        });

        return () => {
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        if (ModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [ModalOpen]);

    useEffect(() => {
        if (!showServicesIntroPopup) return undefined;
        const handleEscape = (event) => {
            if (event.key === 'Escape') dismissServicesIntro();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [showServicesIntroPopup]);

    useEffect(() => {
        // Wait for the onboarding flow to mark a first-time user after its
        // account-history check completes.
        if (localStorage.getItem(getServicesIntroDismissedKey()) === 'true') return undefined;

        let checks = 0;
        const checkForFirstVisit = () => {
            checks += 1;
            if (localStorage.getItem('token') && localStorage.getItem('docity_onboarding_active') === 'true') {
                setShowServicesIntroPopup(true);
                return true;
            }
            return checks >= 240;
        };

        if (checkForFirstVisit()) return undefined;
        const intervalId = window.setInterval(() => {
            if (checkForFirstVisit()) window.clearInterval(intervalId);
        }, 250);
        return () => window.clearInterval(intervalId);
    }, []);
    const [ShowPrevousData, setShowPrevousData] = useState(false)
    useEffect(() => {
        if (ShowPrevousData) {
            openModal();
        }
    }, [ShowPrevousData]);
    const onClickYes = () => {
        setShowPrevousData(true);
        setPaymentModel(false);
        openModal();
        window.dispatchEvent(new CustomEvent('docity_onboarding_jump_to_submit'));
    };
    const onClickNo = async () => {
        if (propertyRequestSwitch === 'Request Property') {
            setPropertyRequestSwitch('Request Property');
            const userDetails = JSON.parse(localStorage.getItem('userDetails'));
            const obj = { userId: userDetails?.user_id };
            const findProperty = await PropertiesService.getUserProperties(obj);
            const findFilledOrNot = findProperty?.data?.filter((x) => x.isFilled === false);
            if (findFilledOrNot?.length > 0) {
                const delObj = { deleteId: findFilledOrNot[0]?._id, deleteItem: 'requestProperty' };
                await PropertiesService.DeleteProperty(delObj);
                openModal();
            }
        } else {
            setPropertyRequestSwitch('Know Your Property');
            const userDetails = JSON.parse(localStorage.getItem('userDetails'));
            const obj = { userId: userDetails?.user_id };
            const findProperty = await PropertiesService.getKpiLists(obj);
            const findFilledOrNot = findProperty?.data?.filter((x) => x.isFilled === false);
            if (findFilledOrNot?.length > 0) {
                const delObj = { deleteId: findFilledOrNot[0]?._id, deleteItem: 'kyp' };
                await PropertiesService.DeleteProperty(delObj);
                openModal();
            }
        }
    };
    /* Primary call to action. Signed-in users go straight to the DCP flow;
       everyone else gets the auth modal first. Same behaviour as the service
       cards below — this only surfaces it above the fold. */
    const startVerification = () => {
        if (token) {
            navigate('/requestProperty');
        } else {
            setIsSignup('firstpage');
            openModal();
        }
    };

    const scrollToServices = (showDcpPopup = true) => {
        setShowDcpServicesPopup(showDcpPopup && localStorage.getItem('docity_onboarding_active') !== 'true');
        document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const continueToServices = () => {
        dismissServicesIntro();
        scrollToServices();
    };

    return (
        /* .ds-marketing steps the whole type scale up (17px base) and enforces a
           48px minimum tap target. Scoped here so the signed-in dashboards keep
           their denser scale. */
        <div className="ds-marketing docity-landing">
            {/* ================================================================
                HERO
                ----------------------------------------------------------------
                The right column used to be a stock skyscraper, which said
                nothing about property verification in India and was the most
                generic thing on the page. It is now a specimen of the artefact
                the service actually produces: a certificate bearing the DoCity
                Seal. Showing the deliverable is the most institutional move
                available, and it is specific to this product.
               ================================================================ */}
            <section className="prestige-hero-section">
                <div className="prestige-hero-container">
                    <div className="hero-left-column animate-fadeIn">
                        {/* The registered entity, not a marketing slogan. On an
                            institutional page the legal name is the credential. */}
                        <span className="hero-eyebrow">
                            DoCity Civil Engineers Private Limited
                        </span>

                        <h1 className="hero-title">
                            Simplify your property journey
                            <img src={logo} className="logostyle" alt="DoCity" />
                        </h1>

                        <p className="hero-description">
                            DoCity simplifies important property decisions through structured digital services designed for both buyers and property owners. Whether you are verifying a property before purchase, understanding your existing property, or creating a self-declared property record, DoCity helps you move forward with greater clarity and confidence..
                        </p>

                        <div className="hero-button-group">
                            <button
                                type="button"
                                className="btn-hero-primary"
                                onClick={startVerification}
                            >
                                Submit a property for review
                                <ArrowRight size={18} aria-hidden="true" />
                            </button>
                            <div className="services-button-anchor">
                                <button
                                    type="button"
                                    className="btn-hero-outline"
                                    onClick={() => {
                                        dismissServicesIntro();
                                        scrollToServices(false);
                                    }}
                                >
                                    Our Services
                                </button>
                                {showServicesIntroPopup && (
                                    <section className="services-intro-popup" role="dialog" aria-labelledby="services-intro-title">
                                        <span className="services-intro-label">Services</span>
                                        <h2 id="services-intro-title">Explore DoCity services</h2>
                                        <p>Choose Continue to view the three property services and select the one you need.</p>
                                        <div className="services-intro-footer">
                                            <div className="services-intro-dots" aria-hidden="true">
                                                {Array.from({ length: 7 }, (_, index) => (
                                                    <span key={index} className={index === 0 ? 'active' : ''} />
                                                ))}
                                            </div>
                                            <button type="button" className="services-intro-skip" onClick={dismissServicesIntro}>
                                                Skip
                                            </button>
                                            <button type="button" className="services-intro-next" onClick={continueToServices}>
                                                Continue <ArrowRight size={16} aria-hidden="true" />
                                            </button>
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>

                        {/* Register of what the service covers. Set as a formal
                            list with rules rather than pill-shaped chips. */}
                        <dl className="hero-register">
                            <div className="hero-register__row">
                                <dt>SERVICES</dt>
                                <dd>DoCity Customer Property (DCP) , Know Your Property (KYP) and Self Occupation Certificate (SOC)</dd>
                            </div>
                            <div className="hero-register__row">
                                <dt>BUILT FOR</dt>
                                <dd>Property buyers and existing property owners</dd>
                            </div>
                            <div className="hero-register__row">
                                <dt>EXPERT SUPPORT</dt>
                                <dd>Civil engineer-led verification through DCP</dd>
                            </div>
                            <div className="hero-register__row">
                                <dt>DIGITAL OUTCOMES</dt>
                                <dd>Service-specific property records, reports and certificates</dd>
                            </div>
                        </dl>
                    </div>

                    <div className="hero-right-column animate-fadeIn">
                        {/* Certificate specimen. Deliberately marked as a specimen so
                            it is never mistaken for a real, issued certificate. */}
                        <figure className="specimen">
                            <div className="specimen__sheet">
                                <span className="specimen__watermark" aria-hidden="true">
                                    <DocitySeal size={280} muted />
                                </span>

                                <div className="specimen__head">
                                    <img src={logo} alt="" className="specimen__logo" aria-hidden="true" />
                                    <span className="specimen__stamp">Specimen</span>
                                </div>

                                <p className="specimen__kicker">Certificate of verification</p>
                                <h2 className="specimen__title">Know Your Property</h2>

                                <dl className="specimen__fields">
                                    <div>
                                        <dt>Property</dt>
                                        <dd>Plot 118, Srinagar Colony, Hyderabad</dd>
                                    </div>
                                    <div>
                                        <dt>Verified by</dt>
                                        <dd>DoCity</dd>
                                    </div>
                                    <div>
                                        <dt>Reference</dt>
                                        <dd className="specimen__mono">DC-KYP-000000</dd>
                                    </div>
                                </dl>

                                <div className="specimen__foot">
                                    <DocitySeal size={92} code="DC-000000" />
                                    <div className="specimen__sign">
                                        <span className="specimen__sign-line" />
                                        <span className="specimen__sign-label">Authorised signature</span>
                                    </div>
                                </div>
                            </div>
                            <figcaption className="specimen__caption">
                                DoCity <span className="specimen__caption-highlight">certifies and Seal</span> represent the outcome of the applicable service and its verification framework. The scope of assessment differs by service: <strong>DCP</strong> includes an engineer-led verification journey, while <strong>SOC</strong> is based on user-submitted documents and self-declaration, and <strong>KYP</strong> follows its own property-information and certificate process. The DoCity Seal is a DoCity-issued mark and should not be interpreted as a government guarantee of title.
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            {/* ================================================================
                WHY PROPERTY CHECKS MATTER
                Explains the real risks of unverified property in India.
               ================================================================ */}
            <section className="why-checks-section">
                <div className="why-checks-section__inner">
                    <div className="why-checks-section__head">
                        <span className="why-checks-section__label">PROPERTY AWARENESS</span>
                        <h2 className="why-checks-section__title">Why property checks matter</h2>
                    </div>
                    <div className="why-checks-section__content">
                        <p className="why-checks-section__body">
                            In India, a property can carry problems you cannot see just by looking at it. The person selling may not have the full right to sell. The title may be disputed. The construction may break local building rules, or the land may be marked for a different use under the city plan. These problems are expensive and slow to fix once you have paid. DoCity's job is to find them before you commit, so a big decision does not turn into a costly mistake.
                        </p>
                    </div>
                </div>
            </section>

            {/* ================================================================
                CREDENTIALS STRIP — HOW WE VERIFY & WORKING ALONGSIDE
                Real, checkable affiliations and verification methodology.
               ================================================================ */}
            <section className="credentials">
                <div className="credentials__inner">
                    <div className="credentials__verify">
                        <span className="credentials__label">How we verify</span>
                        <p className="credentials__body">
                            DoCity is built to bring greater clarity, structure and confidence to the property journey. Through DCP, property buyers receive dedicated support from a qualified civil engineer who reviews relevant property documents, ownership, landuse and planning information, and guides them through the verification process before they proceed. For property owners, KYP and SOC provide purpose-built digital journeys for property information and self-declaration respectively, without engineer-led assessment. Each service follows its own defined process and delivers an outcome aligned with the customer’s specific property need
                        </p>
                    </div>
                    {/* <div className="credentials__partners">
                        <span className="credentials__label">Working alongside</span>
                        <ul className="credentials__list">
                            <li>
                                <a
                                    href="https://rerait.telangana.gov.in"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img src={partner1} alt="Telangana RERA" />
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://rerait.telangana.gov.in"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img src={partner2} alt="Government of Telangana" />
                                </a>
                            </li>
                        </ul>
                    </div> */}
                </div>
            </section>

            {/* ================================================================
                SERVICES — DCP / SOC / KYP
                One card per service, driven by SERVICES below so the three stay
                structurally identical. `id="services"` is the hero's scroll
                target.

                NOTE ON COPY: the description strings are preserved verbatim from
                the original page. They read as form-declaration text pasted into
                marketing cards ("I am above 18 years old. My Aadhaar number"),
                and they should get a copywriting pass — but rewriting claims
                about what a Self Occupied Certificate legally is, is a business
                decision, not a design one. Flagged in DESIGN-SYSTEM.md §9.
               ================================================================ */}
            <section className="portals-city-section" id="services">
                <div className="portals-city-overlay" />

                {/* Editorial header: label and heading on the left, the intro set
                    across the right. Centred headers with a centred sub-line
                    under them are the most dated pattern on a landing page. */}
                <header className="section-head section-head--dark">
                    <div className="section-head__lead">
                        <span className="section-head__label">Services</span>
                        <h2 className="section-head__title">
                            Three ways to be
                            <br />
                            certain about a property.
                        </h2>
                    </div>
                    <p className="section-head__intro">
                        Whether you’re buying a property or already own one, DoCity offers a service designed for your needs. DCP provides engineer-led property verification, KYP helps organise property information, and SOC provides a structured self-declaration and certification process.
                    </p>
                </header>

                {/* Asymmetric on purpose. The recommended service gets more width
                    and a different surface, so the hierarchy is in the layout
                    rather than resting on a badge. */}
                <div className="portals-city-inner">
                    {SERVICES.map(({ key, icon: Icon, title, abbr, description, route, popular }) => (
                        <article
                            key={key}
                            className={`portal-city-card${popular ? ' portal-city-card-popular' : ''}`}
                            data-onboarding={key === 'dcp' ? 'dcp-card' : undefined}
                        >
                            {key === 'dcp' && showDcpServicesPopup && (
                                <div className="dcp-services-popup" role="status" aria-label="DCP service information">
                                    <button
                                        type="button"
                                        className="dcp-services-popup-close"
                                        aria-label="Close DCP information"
                                        onClick={() => setShowDcpServicesPopup(false)}
                                    >
                                        ×
                                    </button>
                                    <span className="dcp-services-popup-label">DCP</span>
                                    <h4>DoCity Customer Property</h4>
                                    <p>Get an engineer-led property review, document assessment and market valuation.</p>
                                    <button type="button" className="dcp-services-popup-action" onClick={() => handleServiceEntry('/requestProperty')}>
                                        Start DCP <ArrowRight size={16} aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                            <div className="portal-city-card-header">
                                <span className="portal-city-abbr">{abbr}</span>
                                {popular && <span className="portal-popular-tag">Recommended</span>}
                            </div>

                            <h3 className="portal-city-title">{title}</h3>

                            <p className="portal-city-desc">{description}</p>

                            <button
                                type="button"
                                className="portal-city-btn"
                                data-onboarding-service={key}
                                data-onboarding={key === 'dcp' ? 'dcp-button' : undefined}
                                onClick={() => handleServiceEntry(route)}
                            >
                                <span>{token ? 'Continue' : 'Get started'}</span>
                                <ArrowRight size={18} aria-hidden="true" />
                            </button>

                            <span className="portal-city-mark" aria-hidden="true">
                                <Icon size={148} strokeWidth={0.6} />
                            </span>
                        </article>
                    ))}
                </div>
            </section>

            {/* ================================================================
                PROCESS — six steps
                Driven by PROCESS_STEPS below. The original six cards each ended
                in a decorative accent — three grey dots, a fake progress bar, or
                three fake avatar initials "R / E / D" — that carried no meaning
                and implied state that does not exist. Replaced with a numbered
                step marker and a connector line, which is the actual
                information: these happen in order.
               ================================================================ */}
            <section className="process-section" id="process">
                <header className="section-head">
                    <div className="section-head__lead">
                        <span className="section-head__label">Process</span>
                        <h2 className="section-head__title">
                            Six steps, start
                            <br />
                            to certificate.
                        </h2>
                    </div>
                    <p className="section-head__intro">
                        You are told what is happening at every stage, and you can reach your
                        assigned engineer throughout.
                    </p>
                </header>

                {/* A numbered sequence of full-width rows separated by hairlines,
                    rather than six identical boxes in a grid. Steps happen in
                    order, so they are set as a list you read down — which also
                    gives each one room for a readable line length. */}
                <ol className="process-list">
                    {PROCESS_STEPS.map(({ icon: Icon, title, desc }, index) => (
                        <li className="process-step" key={title}>
                            <span className="process-step__num" aria-hidden="true">
                                {String(index + 1).padStart(2, '0')}
                            </span>
                            <div className="process-step__body">
                                <h3 className="process-step__title">
                                    <span className="process-step__icon" aria-hidden="true">
                                        <Icon size={17} />
                                    </span>
                                    {title}
                                </h3>
                                <p className="process-step__desc">{desc}</p>
                            </div>
                        </li>
                    ))}
                </ol>

                {/* <div className="process-cta">
                    <div className="process-cta__text">
                        <h3>Ready to start?</h3>
                        <p>Submit a property and we will assign a civil engineer to your case.</p>
                    </div>
                    <button
                        type="button"
                        className="btn-hero-primary"
                        onClick={startVerification}
                    >
                        Submit a property for review
                        <ArrowRight size={18} aria-hidden="true" />
                    </button>
                </div> */}
            </section>

            <div className="landing-aboutus-section">
                <LandingAboutus />
            </div>
            <Footer />

            <CustomModel
                isOpen={PaymentModel}
                question={propertyRequestSwitch}
                onYes={onClickYes}
                onNo={onClickNo}
                close={() => setPaymentModel(false)}
            />
            <Modal isOpen={ModalOpen} isSignup={isSignup} setIsSignup={setIsSignup} closeModal={closeModal} ShowPrevousData={ShowPrevousData} otpVerification={otpVerification} propertyRequestSwitch={propertyRequestSwitch} />

        </div>
    );
};

export default Dashboard;
